/**
 * WebRTC mesh session for one call.
 *
 * Signalling travels on the `call:<id>` Phoenix topic; media is peer-to-peer
 * between participants. See protocol/calls-v1.md and
 * protocol/calls-v2-screen-sharing.md.
 *
 * What the first version got wrong, and this one holds to: a candidate that
 * arrives before the answer is kept, not thrown; "connected" is said only
 * when media is known to be flowing, per peer; a peer that fails is retried
 * with an ICE restart rather than left silent; someone who leaves is taken
 * off the screen; and a muted microphone is announced so the others can see
 * it rather than wonder.
 *
 * A shared screen is a second video track on the same connections, never a
 * second connection. Two things follow. The receiver cannot tell which of a
 * peer's streams is the screen by looking at it, so the sharer names its
 * stream id when it claims the surface and the server hands that name to
 * everyone. And a peer that never said it could render one is not sent it at
 * all — an older client keys remote media by user, so a screen would quietly
 * replace the face it was showing.
 */

import { Socket, type Channel } from 'phoenix'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchCall, fetchIceServers, joinCall, leaveCall, leaveCallOnUnload } from '@/api/calls'
import { SocketTokenKeeper } from '@/api/socketToken'
import { isCallDeviceKind, type Call, type CallDeviceKind, type CallShareSource, type UUID } from '@/api/types'
import { PeerNegotiator, SignalQueue, isDescriptionPayload, isPolite } from '@/hooks/callNegotiation'
import {
  classifyDisplayError,
  classifyMediaError,
  displayErrorMessage,
  mediaErrorMessage,
  openLocalMedia,
  pagePolicy,
} from '@/hooks/callMedia'
import {
  ICE_REFRESH_LEAD_MS,
  PeerWatchdog,
  departedPeerIds,
  iceCandidatePayload,
  iceRefreshDelay,
  joinedPeerIds,
  participantSessionId,
  readIceCandidate,
  readJoinRefusal,
  replacedPeerIds,
} from '@/hooks/callPeers'
import { ownParticipant } from '@/hooks/callRinging'
import { MEETING_FULL } from '@/lib/callCopy'

export interface RemoteTrack {
  userId: UUID
  stream: MediaStream
}

/** What a peer last said about its own microphone and camera. */
export interface RemoteMedia {
  audioMuted: boolean
  videoEnabled: boolean
}

/** Who holds the call's one shared surface, as the server last said. */
export interface ShareState {
  userId: UUID
  streamId: string | null
  source: CallShareSource | null
  audio: boolean
}

/** How a session stops that is not this device leaving (calls-v1 §7.3). */
export type SessionEnd = { reason: 'replaced'; deviceKind: CallDeviceKind | null } | { reason: 'ended' }

export interface CallSessionState {
  localStream: MediaStream | null
  remoteTracks: RemoteTrack[]
  /** The signalling channel is joined. Says nothing about media. */
  connected: boolean
  /** Per peer, the WebRTC connection state — the thing that does. */
  peerStates: Record<UUID, RTCPeerConnectionState>
  remoteMedia: Record<UUID, RemoteMedia>
  error: string | null
  micEnabled: boolean
  cameraEnabled: boolean
  /** Peers whose voice is carrying right now, debounced. */
  speaking: Record<UUID, boolean>
  /**
   * The browser says it is holding video back for want of bandwidth. Its own
   * word for it, not a guess from a bitrate we would have had to invent a
   * target for.
   */
  constrained: boolean
  /**
   * The camera was switched off to keep the audio, not by the person. Audio
   * is the meeting; video is decoration. Saying which of the two happened is
   * the difference between a client that looks broken and one that explains
   * itself.
   */
  videoPausedForBandwidth: boolean
  share: ShareSessionState
  /**
   * Why the session stopped without anyone here hanging up: another device
   * took the seat, or the call ended under it. Null while it runs.
   */
  sessionEnd: SessionEnd | null
  /** Peers that never came up after an ICE restart; their tiles say so. */
  unreachable: Record<UUID, boolean>
  /**
   * The microphone or camera stopped under the call — unplugged, taken by a
   * phone call, paused by the system — and could not be put back. Without
   * this the stage goes on saying Mute to someone nobody can hear.
   */
  deviceNotice?: string | null
}

export interface ShareSessionState {
  /** This device can capture a screen at all. */
  supported: boolean
  /** Who is sharing, from the server. Null when nobody is. */
  state: ShareState | null
  /** What we are sending, when it is us. */
  local: MediaStream | null
  /** What we are receiving, when it is someone else. */
  remote: MediaStream | null
  /** Set when a claim was refused because someone else holds the surface. */
  conflict: boolean
  /** The last claim failed for a reason worth showing. */
  error: string | null
}

export interface StartShareOptions {
  audio?: boolean
  takeover?: boolean
}

/**
 * What the person chose before joining. The microphone track is always
 * acquired and muted when it is off, so unmuting mid-call needs no
 * renegotiation. The camera is not: asking for a camera someone has just
 * declined turns their light on for nothing.
 */
export interface CallPreferences {
  mic: boolean
  camera: boolean
  /** A specific microphone, from enumerateDevices; the default when unset. */
  audioDeviceId?: string
  videoDeviceId?: string
  /**
   * Chosen in the pre-join, acted on once the call is up. Whoever is about to
   * present knows it beforehand and should not have to hunt for the button
   * with everyone watching.
   */
  shareOnJoin?: boolean
}

const DEFAULT_PREFERENCES: CallPreferences = { mic: true, camera: true }

const SOCKET_PATH = '/socket/v1'

/** A channel that has not answered by now is not going to (calls-v1 §7.6). */
const CHANNEL_JOIN_TIMEOUT_MS = 15_000

/** Under the server's 45 second idle limit, with room for a slow line (calls-v1 §7.7). */
const HEARTBEAT_MS = 25_000

/** Voice this loud, for this long, is someone speaking rather than breathing. */
const SPEAKING_LEVEL = 0.02
const SPEAKING_IN_MS = 400
const SPEAKING_OUT_MS = 1000
const LEVEL_POLL_MS = 200

/** The browser has to say it is bandwidth-bound for this long before we do. */
const CONSTRAINED_FOR_MS = 5000
const STATS_POLL_MS = 1000

/**
 * A camera is worth a quarter of the resolution while a screen is going out.
 * The screen is the part that has to stay readable; a mesh carrying eight
 * cameras and a 1080p screen does not fit down an ordinary office line.
 */
const CAMERA_SCALE_WHILE_SHARING = 4
const CAMERA_BITRATE_WHILE_SHARING = 120_000
const CAMERA_FRAMERATE_WHILE_SHARING = 15

/** Whether this device can capture a screen at all. Phones cannot, yet. */
export function screenShareSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  )
}

/** Whether a screen track may be sent to this participant. */
export function canRenderShare(call: Call | null, userId: UUID): boolean {
  if (call === null) return false
  return call.participants.some(
    (participant) => participant.user?.id === userId && (participant.features ?? []).includes('screen_share'),
  )
}

/** What a session needs from its owner beyond the call itself. */
export interface CallSessionOptions {
  /**
   * This device's part in the call (calls-v1 §4.3), made fresh for every
   * attempt to join. Null until there is an attempt to make.
   */
  sessionId: string | null
  /** The channel came back after a dropped line; the call should be read again. */
  onRejoin?: () => void
}

const NO_SESSION: CallSessionOptions = { sessionId: null }

/** A per-peer record without one peer, or the same record when it was not there. */
function withoutPeer<T>(record: Record<UUID, T>, remoteId: UUID): Record<UUID, T> {
  if (!(remoteId in record)) return record
  const { [remoteId]: _gone, ...rest } = record
  return rest
}

/** Which of a peer's streams is its shared screen, when it is sharing one. */
function shareStreamOf(streams: readonly MediaStream[], remoteId: UUID, share: ShareState | null): MediaStream | null {
  if (share === null || share.userId !== remoteId) return null
  if (share.streamId !== null) return streams.find((entry) => entry.id === share.streamId) ?? null
  // Before the stream is named — a reload mid-share — the last stream is the
  // only candidate there is.
  return streams.length > 1 ? (streams[streams.length - 1] ?? null) : null
}

export function useCallSession(
  call: Call | null,
  userId: UUID | undefined,
  enabled: boolean,
  preferences: CallPreferences = DEFAULT_PREFERENCES,
  options: CallSessionOptions = NO_SESSION,
): CallSessionState & {
  hangUp: () => Promise<void>
  setMicEnabled: (on: boolean) => void
  setCameraEnabled: (on: boolean) => void
  startShare: (options?: StartShareOptions) => Promise<void>
  stopShare: () => Promise<void>
  dismissShareError: () => void
  retryPeer: (remoteId: UUID) => void
  retryDevices: () => void
} {
  const sessionId = options.sessionId
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remoteStreams, setRemoteStreams] = useState<Record<UUID, MediaStream[]>>({})
  const [connected, setConnected] = useState(false)
  const [peerStates, setPeerStates] = useState<Record<UUID, RTCPeerConnectionState>>({})
  const [remoteMedia, setRemoteMedia] = useState<Record<UUID, RemoteMedia>>({})
  const [error, setError] = useState<string | null>(null)
  const [micEnabled, setMicState] = useState(preferences.mic)
  const [cameraEnabled, setCameraState] = useState(preferences.camera)
  const [speaking, setSpeaking] = useState<Record<UUID, boolean>>({})
  const [constrained, setConstrained] = useState(false)
  const [videoPausedForBandwidth, setVideoPaused] = useState(false)
  const [shareState, setShareState] = useState<ShareState | null>(null)
  const [localShare, setLocalShare] = useState<MediaStream | null>(null)
  const [shareConflict, setShareConflict] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)
  const [sessionEnd, setSessionEnd] = useState<SessionEnd | null>(null)
  const [unreachable, setUnreachable] = useState<Record<UUID, boolean>>({})
  const [deviceNotice, setDeviceNotice] = useState<string | null>(null)

  // Read inside the effect so changing a toggle mid-call does not tear the
  // session down and dial again.
  const preferencesRef = useRef(preferences)
  preferencesRef.current = preferences

  const callRef = useRef<Call | null>(call)
  callRef.current = call

  const socketRef = useRef<Socket | null>(null)
  const channelRef = useRef<Channel | null>(null)
  const peersRef = useRef<Map<UUID, RTCPeerConnection>>(new Map())
  const negotiatorsRef = useRef<Map<UUID, PeerNegotiator>>(new Map())
  const queueRef = useRef(new SignalQueue())
  // Which of a person's devices each connection goes to (calls-v1 §4.3). Null
  // for a peer reached before its session was known.
  const peerSessionsRef = useRef<Map<UUID, string | null>>(new Map())
  const onRejoinRef = useRef(options.onRejoin)
  onRejoinRef.current = options.onRejoin
  const localStreamRef = useRef<MediaStream | null>(null)
  const localShareRef = useRef<MediaStream | null>(null)
  const shareSendersRef = useRef<Map<UUID, RTCRtpSender[]>>(new Map())
  // The stream each peer's camera and microphone went out in. A track added
  // later goes out in the same one, so the far side keeps one face per person
  // rather than a voice in one stream and a picture in another.
  const sentStreamsRef = useRef<Map<UUID, MediaStream>>(new Map())
  // Counts sessions ended. A device opened for one is not published in the
  // next: a camera that answers after the meeting has gone is switched off.
  const sessionGenerationRef = useRef(0)
  // The faces made of more than one stream, kept so a re-render does not
  // hand every tile and speaker a new stream to start over with.
  const mergedFacesRef = useRef<Map<UUID, { key: string; stream: MediaStream }>>(new Map())
  const renegotiateRef = useRef<(remoteId: UUID) => void>(() => undefined)
  const attachShareRef = useRef<(remoteId: UUID) => void>(() => undefined)
  const watchdogRef = useRef<PeerWatchdog | null>(null)
  const offerRef = useRef<(remoteId: UUID) => void>(() => undefined)
  const tokensRef = useRef<SocketTokenKeeper | null>(null)
  const iceTimerRef = useRef<number | null>(null)
  const watchLocalRef = useRef<(track: MediaStreamTrack) => void>(() => undefined)
  // Stops the join under way, for a hang-up that lands before it is done.
  const cancelRunRef = useRef<() => void>(() => undefined)

  const closePeer = useCallback((remoteId: UUID) => {
    const peer = peersRef.current.get(remoteId)
    if (peer === undefined) return
    peer.onicecandidate = null
    peer.ontrack = null
    peer.onconnectionstatechange = null
    peer.close()
    peersRef.current.delete(remoteId)
    negotiatorsRef.current.delete(remoteId)
    peerSessionsRef.current.delete(remoteId)
    watchdogRef.current?.forget(remoteId)
    setUnreachable((current) => withoutPeer(current, remoteId))
    shareSendersRef.current.delete(remoteId)
    sentStreamsRef.current.delete(remoteId)
    setRemoteStreams((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
    setPeerStates((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
    setRemoteMedia((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
    setSpeaking((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
  }, [])

  const stopLocalShareTracks = useCallback(() => {
    localShareRef.current?.getTracks().forEach((track) => track.stop())
    localShareRef.current = null
    setLocalShare(null)
  }, [])

  const cleanup = useCallback(() => {
    sessionGenerationRef.current += 1
    channelRef.current?.leave()
    channelRef.current = null
    socketRef.current?.disconnect()
    socketRef.current = null
    tokensRef.current?.stop()
    tokensRef.current = null
    if (iceTimerRef.current !== null) window.clearTimeout(iceTimerRef.current)
    iceTimerRef.current = null

    for (const remoteId of [...peersRef.current.keys()]) closePeer(remoteId)

    watchdogRef.current?.stop()
    watchdogRef.current = null
    setUnreachable({})

    localStreamRef.current?.getTracks().forEach((track) => {
      track.onended = null
      track.onmute = null
      track.onunmute = null
      track.stop()
    })
    localStreamRef.current = null
    setDeviceNotice(null)
    stopLocalShareTracks()
    shareSendersRef.current.clear()
    setLocalStream(null)
    setRemoteStreams({})
    setPeerStates({})
    setRemoteMedia({})
    setSpeaking({})
    setShareState(null)
    setShareConflict(false)
    setShareError(null)
    setConstrained(false)
    setVideoPaused(false)
    setConnected(false)
    setSessionEnd(null)
    // A failure belongs to the session it ended. `fail` sets its message
    // after this, so the next pre-join or meeting starts without it.
    setError(null)
  }, [closePeer, stopLocalShareTracks])

  const hangUp = useCallback(async () => {
    if (call === null) return
    // The session stays enabled until the leave has gone out. A join still
    // waiting on the camera must not open it, and a socket, in the meantime.
    cancelRunRef.current()
    cleanup()
    await leaveCall(call.id, sessionId)
  }, [call, cleanup, sessionId])

  // "Try again" on a tile: a new connection from scratch, and a new clock.
  const retryPeer = useCallback(
    (remoteId: UUID) => {
      closePeer(remoteId)
      offerRef.current(remoteId)
    },
    [closePeer],
  )

  // The server's participant list drives who we hold a connection to. A
  // newly joined peer offers to us, so only departures are acted on here —
  // and a person now in the call from another device has departed from the
  // device we were connected to.
  useEffect(() => {
    if (call === null) return
    for (const remoteId of departedPeerIds(peersRef.current.keys(), call, userId)) {
      closePeer(remoteId)
    }
    for (const remoteId of replacedPeerIds(peerSessionsRef.current, call, userId)) {
      closePeer(remoteId)
    }
  }, [call, closePeer, userId])

  // The call itself carries who is sharing, so a tab that reloads mid-share,
  // or someone who joined after it started, is not left waiting for a signal
  // that already went out.
  useEffect(() => {
    if (call === null) return
    const holder = call.sharing_user_id ?? null
    setShareState((current) => {
      if (holder === null) return null
      if (current !== null && current.userId === holder && current.streamId !== null) return current
      return {
        userId: holder,
        streamId: call.sharing_stream_id ?? null,
        source: call.sharing_source ?? null,
        audio: false,
      }
    })
  }, [call])

  const callId = call?.id
  const sharingRef = useRef(false)

  useEffect(() => {
    if (!enabled || callId === undefined || userId === undefined || sessionId === null) {
      cleanup()
      return
    }

    // Narrowed once, for the closures below.
    const selfId = userId

    let cancelled = false
    cancelRunRef.current = () => {
      cancelled = true
    }
    // Set once the server holds a seat for this session. From then on a
    // failure gives it back (calls-v1 §7.9): a device that joined and then
    // could not open its camera would otherwise sit in the call, silent,
    // until the server's clean-up noticed.
    let admitted = false

    const fail = (message: string): void => {
      if (cancelled) return
      cancelled = true
      cleanup()
      setError(message)
      if (admitted) void leaveCall(callId, sessionId).catch(() => undefined)
    }

    // The call went on without this session: another device took it, or it
    // ended. Media stops and nothing is sent — least of all a leave, which
    // would be the old device hanging up on the new one.
    const stop = (end: SessionEnd): void => {
      if (cancelled) return
      cancelled = true
      cleanup()
      setSessionEnd(end)
    }

    const run = async (): Promise<void> => {
      try {
        const joined = await joinCall(callId, sessionId)
        admitted = true
        // Torn down while the join was out — hung up, or the session let go
        // — so the seat the server has just given is given straight back.
        // A leave sent before it would have arrived ahead of the seat.
        if (cancelled) {
          void leaveCall(callId, sessionId).catch(() => undefined)
          return
        }

        let ice = await fetchIceServers()
        if (cancelled) return

        // TURN credentials last minutes; a meeting lasts longer. Fresh ones go
        // to every connection before the old ones lapse, so an ICE restart
        // late in a call still finds a relay that lets it in (calls-v1 §7.8).
        const refreshIce = (expiresAt: string): void => {
          const delay = iceRefreshDelay(expiresAt, Date.now())
          if (delay === null || cancelled) return
          iceTimerRef.current = window.setTimeout(() => {
            void fetchIceServers()
              .then((fresh) => {
                if (cancelled) return
                ice = fresh
                for (const peer of peersRef.current.values()) {
                  try {
                    peer.setConfiguration({ ...peer.getConfiguration(), iceServers: fresh.ice_servers })
                  } catch (caught) {
                    console.warn('ICE servers could not be updated', caught)
                  }
                }
                refreshIce(fresh.expires_at)
              })
              .catch(() => {
                // Asked again shortly; the credentials in hand still work until they lapse.
                refreshIce(new Date(Date.now() + ICE_REFRESH_LEAD_MS).toISOString())
              })
          }, delay)
        }
        refreshIce(ice.expires_at)

        const wanted = preferencesRef.current
        const video = joined.call.media === 'video' && wanted.camera
        // A camera unplugged since the preview, or a remembered device that
        // has gone, is not a reason to fail the join: the call goes ahead on
        // what is there, and the stage offers no video switch without a camera.
        const opened = await openLocalMedia(
          (constraints) => navigator.mediaDevices.getUserMedia(constraints),
          { audioDeviceId: wanted.audioDeviceId, videoDeviceId: wanted.videoDeviceId, video },
          true,
        ).catch((caught: unknown) => {
          // Refused after joining, the devices have the same fixes as in the
          // pre-join, and the person is told the same thing (calls-v1 §8).
          fail(mediaErrorMessage(classifyMediaError(caught, pagePolicy(), video), video))
          return null
        })
        if (opened === null) return
        const { stream } = opened
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        stream.getAudioTracks().forEach((track) => (track.enabled = wanted.mic))
        // A camera is motion; a shared screen is detail. Telling the encoder
        // which costs nothing and is the difference between readable text and
        // a smooth blur.
        stream.getVideoTracks().forEach((track) => (track.contentHint = 'motion'))
        setMicState(wanted.mic)
        setCameraState(stream.getVideoTracks().length > 0)

        localStreamRef.current = stream
        setLocalStream(stream)
        stream.getTracks().forEach((track) => watchLocalRef.current(track))

        const tokens = new SocketTokenKeeper()
        tokensRef.current = tokens
        await tokens.refresh()
        if (cancelled) return

        const socket = new Socket(SOCKET_PATH, {
          // A function, read again on every reconnect, so a meeting that
          // outlives one socket token reconnects with the next rather than
          // replaying an expired one for ever (calls-v1 §7.7).
          params: () => {
            const token = tokens.current()
            return token === null ? {} : { token }
          },
          heartbeatIntervalMs: HEARTBEAT_MS,
        })
        // A connection that failed may have failed on its token; the next
        // attempt should not have to find out the same way.
        socket.onError(() => {
          void tokens.refresh().catch(() => undefined)
        })
        socketRef.current = socket
        socket.connect()

        const channel = socket.channel(`call:${callId}`, { session_id: sessionId })
        channelRef.current = channel

        const sendSignal = (targetUserId: UUID, kind: string, payload: Record<string, unknown>): void => {
          channel.push('signal', { target_user_id: targetUserId, kind, payload })
        }

        const announceMedia = (): void => {
          const audio = localStreamRef.current?.getAudioTracks()[0]
          const video = localStreamRef.current?.getVideoTracks()[0]
          channel.push('media', {
            audio_muted: audio === undefined ? true : !audio.enabled,
            video_enabled: video !== undefined && video.enabled,
          })
        }

        // Every offer goes through the peer's queue, so it can never cross an
        // answer being applied to the same connection. Dialling a peer without
        // a connection yet makes one, to the device the call says it is on. A
        // restart or a renegotiation is for the connection that asked for it:
        // if that was closed while the work waited in the queue, the peer has
        // gone, and nothing is to be made in its place.
        const offerTo = (remoteId: UUID, kind: 'dial' | 'restart' | 'renegotiate' = 'dial'): void => {
          void queueRef.current
            .run(remoteId, async () => {
              const held = negotiatorsRef.current.get(remoteId)
              if (held === undefined && kind !== 'dial') return
              const negotiator =
                held ?? ensurePeer(remoteId, participantSessionId(callRef.current ?? joined.call, remoteId))
              await (kind === 'restart' ? negotiator.restart() : negotiator.offer())
            })
            .catch((caught: unknown) => {
              console.warn('offer failed', caught)
            })
        }

        // Adding or removing a screen track changes what the connection
        // carries, so it has to be offered again. The negotiator keeps an
        // offer asked for while another is still out and sends it once that
        // one is answered, so nothing here has to poll for a stable state.
        const renegotiate = (remoteId: UUID): void => {
          if (!peersRef.current.has(remoteId)) return
          offerTo(remoteId, 'renegotiate')
        }
        renegotiateRef.current = renegotiate

        const watchdog = new PeerWatchdog({
          restart: (remoteId) => offerTo(remoteId, 'restart'),
          unreachable: (remoteId) => setUnreachable((current) => ({ ...current, [remoteId]: true })),
        })
        watchdogRef.current = watchdog
        offerRef.current = (remoteId) => offerTo(remoteId)

        // Only to a peer that has said it can draw one. The check is the
        // participant's own word, from the call we are holding.
        const attachShareTo = (remoteId: UUID): void => {
          const share = localShareRef.current
          const peer = peersRef.current.get(remoteId)
          if (share === null || peer === undefined) return
          if (!canRenderShare(callRef.current, remoteId)) return
          if (shareSendersRef.current.has(remoteId)) return

          const senders = share.getTracks().map((track) => peer.addTrack(track, share))
          shareSendersRef.current.set(remoteId, senders)
          renegotiate(remoteId)
        }
        attachShareRef.current = attachShareTo

        const ensurePeer = (remoteId: UUID, remoteSessionId: string | null): PeerNegotiator => {
          const existing = negotiatorsRef.current.get(remoteId)
          if (existing !== undefined) return existing

          const peer = new RTCPeerConnection({ iceServers: ice.ice_servers })
          const negotiator = new PeerNegotiator(peer, isPolite(selfId, remoteId), (kind, payload) =>
            sendSignal(remoteId, kind, payload),
          )
          peersRef.current.set(remoteId, peer)
          negotiatorsRef.current.set(remoteId, negotiator)
          peerSessionsRef.current.set(remoteId, remoteSessionId)
          setPeerStates((current) => ({ ...current, [remoteId]: peer.connectionState }))

          // What is being sent now, not what the call was joined with: a
          // camera started since, or a microphone put back, is part of it.
          const sending = localStreamRef.current ?? stream
          for (const track of sending.getTracks()) {
            peer.addTrack(track, sending)
          }
          sentStreamsRef.current.set(remoteId, sending)

          peer.onicecandidate = (event) => {
            if (event.candidate !== null) {
              sendSignal(remoteId, 'ice', iceCandidatePayload(event.candidate.toJSON()))
            }
          }

          // Every stream a peer sends is kept, not just the first: the second
          // one is a screen, and which is which is answered by the share
          // signal rather than by arrival order.
          peer.ontrack = (event) => {
            const [remoteStream] = event.streams
            if (remoteStream === undefined) return
            setRemoteStreams((current) => {
              const held = current[remoteId] ?? []
              // A track for a stream already held: a camera started after
              // the microphone. The stream is the same; a new list makes
              // what draws it look again and find the picture.
              if (held.some((entry) => entry.id === remoteStream.id)) {
                return {
                  ...current,
                  [remoteId]: held.map((entry) => (entry.id === remoteStream.id ? remoteStream : entry)),
                }
              }
              return { ...current, [remoteId]: [...held, remoteStream] }
            })
          }

          // Thirty seconds to come up, one ICE restart, thirty more, and then
          // the tile says so (calls-v1 §7.6). A `failed` state skips the wait —
          // the browser has already given up — while `disconnected` is
          // transient and left to recover on its own.
          watchdog.watch(remoteId)
          peer.onconnectionstatechange = () => {
            setPeerStates((current) => ({ ...current, [remoteId]: peer.connectionState }))
            if (peer.connectionState === 'failed') watchdog.failed(remoteId)
            if (peer.connectionState === 'connected') {
              watchdog.connected(remoteId)
              setUnreachable((current) => withoutPeer(current, remoteId))
              // A peer that arrives while a screen is already going out gets
              // it as soon as it is there to get it.
              attachShareTo(remoteId)
            }
          }

          return negotiator
        }

        channel.on('signal', (payload: Record<string, unknown>) => {
          const fromUserId = payload['from_user_id']
          const kind = payload['kind']
          const body = payload['payload']
          if (typeof fromUserId !== 'string' || typeof kind !== 'string' || typeof body !== 'object' || body === null) {
            return
          }
          // Our own words coming back. The server delivers a signal only to
          // the device it is for (calls-v1 §6.2); this is a guard, not a path.
          if (fromUserId === selfId) return
          const fromSession = payload['from_session_id']
          const fromSessionId = typeof fromSession === 'string' ? fromSession : null

          void queueRef.current
            .run(fromUserId, async () => {
              // The same person, now on another device: the connection held
              // goes to the one that left, and nothing more will come down it.
              const held = peerSessionsRef.current.get(fromUserId)
              if (held !== undefined && held !== null && fromSessionId !== null && held !== fromSessionId) {
                closePeer(fromUserId)
              } else if (held === null && fromSessionId !== null) {
                peerSessionsRef.current.set(fromUserId, fromSessionId)
              }

              // Only an offer opens a connection. An answer or a candidate for
              // one we do not hold belongs to a connection already gone.
              const negotiator =
                negotiatorsRef.current.get(fromUserId) ??
                (kind === 'offer' ? ensurePeer(fromUserId, fromSessionId) : null)
              if (negotiator === null) return

              if ((kind === 'offer' || kind === 'answer') && isDescriptionPayload(body)) {
                try {
                  await negotiator.receiveDescription(body.sdp)
                } catch (caught: unknown) {
                  // An offer this connection cannot take is an offer for a
                  // new one: the other side pressed Try again, or lost its
                  // connection with its socket, and started from nothing.
                  // Holding on to ours leaves both waiting out the watchdog.
                  if (kind !== 'offer') throw caught
                  closePeer(fromUserId)
                  await ensurePeer(fromUserId, fromSessionId).receiveDescription(body.sdp)
                }
              } else if (kind === 'ice') {
                const candidate = readIceCandidate(body)
                if (candidate !== null) await negotiator.receiveCandidate(candidate)
              }
            })
            .catch((caught: unknown) => {
              // One bad candidate is not the call; it is logged, not shown.
              console.warn('call signal failed', kind, caught)
            })
        })

        channel.on('media', (payload: Record<string, unknown>) => {
          const from = payload['user_id']
          if (typeof from !== 'string' || from === userId) return
          setRemoteMedia((current) => ({
            ...current,
            [from]: {
              audioMuted: payload['audio_muted'] === true,
              videoEnabled: payload['video_enabled'] !== false,
            },
          }))
        })

        // The server owns the answer to who is sharing. Everyone on the topic
        // is told, the sharer included, so no client decides it for itself.
        channel.on('share.changed', (payload: Record<string, unknown>) => {
          const holder = payload['user_id']
          if (typeof holder !== 'string') {
            setShareState(null)
            if (localShareRef.current !== null) stopLocalShareTracks()
            return
          }

          const streamId = payload['stream_id']
          setShareState({
            userId: holder,
            streamId: typeof streamId === 'string' ? streamId : null,
            source: (payload['source'] as CallShareSource | null) ?? null,
            audio: payload['audio'] === true,
          })

          // Someone took the surface from us. The capture stops here rather
          // than going out to nobody.
          if (holder !== userId && localShareRef.current !== null) {
            stopLocalShareTracks()
            for (const [remoteId, senders] of shareSendersRef.current) {
              const peer = peersRef.current.get(remoteId)
              senders.forEach((sender) => {
                try {
                  peer?.removeTrack(sender)
                } catch {
                  // The peer may already be gone; nothing to undo.
                }
              })
              renegotiate(remoteId)
            }
            shareSendersRef.current.clear()
            setShareError('Someone else took over the shared screen.')
          }
        })

        channel.onError(() => {
          if (!cancelled) setConnected(false)
        })

        // Another device took the seat (calls-v1 §7.3). The server closes this
        // channel once it has said so; which device it was is in the event.
        channel.on('session.replaced', (payload: Record<string, unknown>) => {
          const deviceKind = payload['device_kind']
          stop({ reason: 'replaced', deviceKind: isCallDeviceKind(deviceKind) ? deviceKind : null })
        })

        // A rejoin refused for a reason the refusal does not name is, in
        // practice, a line that stayed down longer than the server keeps a
        // seat (calls-v1 §6.5). The call says what became of it: over, taken
        // by another device, or given up. A seat given up is taken back with
        // the same session, and Phoenix's next rejoin is let in.
        let reseating = false
        const reseat = async (): Promise<void> => {
          if (reseating) return
          reseating = true
          try {
            const fresh = await fetchCall(callId, (callRef.current ?? joined.call).room_id).catch(() => undefined)
            // A call that could not be read is asked for again on the next refusal.
            if (cancelled || fresh === undefined) return
            if (fresh === null || fresh.status === 'ended') {
              stop({ reason: 'ended' })
              return
            }
            const mine = ownParticipant(fresh, selfId)
            if (mine !== null && mine.status === 'joined') {
              const holder = mine.session_id ?? null
              if (holder !== null && holder !== sessionId) {
                stop({ reason: 'replaced', deviceKind: mine.device_kind ?? null })
              }
              // Still this session's seat: the refusal was the line's, and Phoenix tries again.
              return
            }
            const retaken = await joinCall(callId, sessionId).then(
              () => true,
              () => false,
            )
            if (!retaken) fail('Could not join the call channel.')
          } finally {
            reseating = false
          }
        }

        // Everyone already in the call gets an offer from us; anyone who
        // joins later offers to us. Phoenix re-sends the join after a
        // dropped socket and this fires again, so a peer whose connection
        // died with the socket is dialled afresh while one that survived is
        // left alone. A rejoin reads who is in from the call as it is now,
        // not as it was when we joined (calls-v1 §8), and asks for the call
        // to be read again: nothing replays what the drop missed.
        let joins = 0
        channel
          .join(CHANNEL_JOIN_TIMEOUT_MS)
          .receive('ok', () => {
            if (cancelled) return
            joins += 1
            setConnected(true)
            announceMedia()
            const current = joins === 1 ? joined.call : (callRef.current ?? joined.call)
            for (const remoteId of joinedPeerIds(current, userId)) {
              const existing = peersRef.current.get(remoteId)
              if (existing !== undefined && existing.connectionState === 'connected') continue
              if (existing !== undefined) closePeer(remoteId)
              offerTo(remoteId)
            }
            if (joins > 1) onRejoinRef.current?.()
          })
          .receive('error', (reply: unknown) => {
            const refusal = readJoinRefusal(reply)
            if (refusal.code === 'session_replaced') {
              // Refused because another device holds the seat. Which one, the
              // call says when the refusal does not.
              stop({
                reason: 'replaced',
                deviceKind:
                  refusal.deviceKind ?? ownParticipant(callRef.current ?? joined.call, selfId)?.device_kind ?? null,
              })
            } else if (refusal.code === 'ended') {
              stop({ reason: 'ended' })
            } else if (joins > 0) {
              void reseat()
            } else {
              fail('Could not join the call channel.')
            }
          })
          .receive('timeout', () => {
            // Only the first join is held to the limit. A rejoin after a
            // dropped line is Phoenix's to keep trying, and the server's
            // clean-up decides when a device that never came back has gone.
            if (joins === 0) fail('Could not join the call channel.')
          })
      } catch (caught) {
        // Refused at the door with nothing wrong in the request: there is no
        // seat. Said as that, not as the server's own words (Meetings.md §8).
        const refusal = caught as { status?: number; code?: string } | null
        if (!admitted && refusal?.status === 400 && refusal.code === 'invalid_request') fail(MEETING_FULL)
        else fail(caught instanceof Error ? caught.message : 'Could not start the call.')
      }
    }

    void run()

    return () => {
      cancelled = true
      cleanup()
    }
  }, [callId, cleanup, closePeer, enabled, sessionId, stopLocalShareTracks, userId])

  // Closing the tab is leaving the meeting. Nothing after `pagehide` is sure
  // to run, so the leave goes out as a keepalive request rather than waiting
  // for the server's clean-up to notice the device has gone.
  useEffect(() => {
    if (!enabled || callId === undefined || sessionId === null) return
    const onPageHide = (): void => leaveCallOnUnload(callId, sessionId)
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [callId, enabled, sessionId])

  // ── Who is speaking ────────────────────────────────────────────────────────
  //
  // From the receivers themselves rather than a web audio graph per peer: the
  // level is already in the RTP header, and reading it there costs nothing
  // where decoding a second copy of every stream would.
  useEffect(() => {
    if (!connected) return

    const since: Record<UUID, number> = {}
    const timer = window.setInterval(() => {
      const now = Date.now()
      const loud: Record<UUID, boolean> = {}

      for (const [remoteId, peer] of peersRef.current) {
        let level = 0
        for (const receiver of peer.getReceivers()) {
          if (receiver.track.kind !== 'audio') continue
          const sources = receiver.getSynchronizationSources?.() ?? []
          for (const source of sources) level = Math.max(level, source.audioLevel ?? 0)
        }
        loud[remoteId] = level >= SPEAKING_LEVEL
      }

      setSpeaking((current) => {
        let changed = false
        const next: Record<UUID, boolean> = { ...current }

        for (const [remoteId, isLoud] of Object.entries(loud)) {
          const wasSpeaking = current[remoteId] === true
          if (isLoud) {
            since[remoteId] = since[remoteId] ?? now
            if (!wasSpeaking && now - (since[remoteId] ?? now) >= SPEAKING_IN_MS) {
              next[remoteId] = true
              changed = true
            }
          } else {
            const quietSince = since[remoteId]
            if (quietSince !== undefined) delete since[remoteId]
            if (wasSpeaking && now - (quietSince ?? now) >= SPEAKING_OUT_MS) {
              next[remoteId] = false
              changed = true
            }
          }
        }

        return changed ? next : current
      })
    }, LEVEL_POLL_MS)

    return () => window.clearInterval(timer)
  }, [connected])

  // ── A line too narrow for what we are sending ─────────────────────────────
  useEffect(() => {
    if (!connected) return

    let limitedSince: number | null = null
    const timer = window.setInterval(() => {
      void (async () => {
        let limited = false
        // While a screen goes out the camera is capped on purpose and the
        // screen takes what the line has. Neither is a narrow line.
        const peersToRead = sharingRef.current ? [] : [...peersRef.current.values()]
        for (const peer of peersToRead) {
          const stats = await peer.getStats().catch(() => null)
          stats?.forEach((report) => {
            const outbound = report as { type?: string; kind?: string; qualityLimitationReason?: string }
            if (outbound.type === 'outbound-rtp' && outbound.kind === 'video') {
              if (outbound.qualityLimitationReason === 'bandwidth') limited = true
            }
          })
        }

        const now = Date.now()
        if (limited) {
          limitedSince = limitedSince ?? now
          if (now - limitedSince >= CONSTRAINED_FOR_MS) setConstrained(true)
        } else {
          limitedSince = null
          setConstrained(false)
        }
      })()
    }, STATS_POLL_MS)

    return () => window.clearInterval(timer)
  }, [connected])

  // ── The camera, while a screen is going out ───────────────────────────────
  const sharingNow = shareState !== null
  sharingRef.current = sharingNow
  useEffect(() => {
    for (const peer of peersRef.current.values()) {
      for (const sender of peer.getSenders()) {
        if (sender.track?.kind !== 'video') continue
        // The screen sender is not capped; it is the thing being read.
        const isShare = [...shareSendersRef.current.values()].some((senders) => senders.includes(sender))
        if (isShare) continue

        const parameters = sender.getParameters()
        if (parameters.encodings === undefined || parameters.encodings.length === 0) continue
        parameters.encodings[0] = {
          ...parameters.encodings[0],
          scaleResolutionDownBy: sharingNow ? CAMERA_SCALE_WHILE_SHARING : 1,
          maxBitrate: sharingNow ? CAMERA_BITRATE_WHILE_SHARING : undefined,
          maxFramerate: sharingNow ? CAMERA_FRAMERATE_WHILE_SHARING : undefined,
        }
        void sender.setParameters(parameters).catch(() => undefined)
      }
    }
  }, [sharingNow])

  const announce = useCallback(() => {
    const channel = channelRef.current
    if (channel === null) return
    const audio = localStreamRef.current?.getAudioTracks()[0]
    const video = localStreamRef.current?.getVideoTracks()[0]
    channel.push('media', {
      audio_muted: audio === undefined ? true : !audio.enabled,
      video_enabled: video !== undefined && video.enabled,
    })
  }, [])

  const setMicEnabled = useCallback(
    (on: boolean) => {
      localStreamRef.current?.getAudioTracks().forEach((track) => (track.enabled = on))
      setMicState(on)
      announce()
    },
    [announce],
  )

  // Puts a track into what every peer is sent: in place of the one of its kind
  // when there is one, as a new one — offered again — when there is not.
  const publishLocalTrack = useCallback((track: MediaStreamTrack): void => {
    const held = localStreamRef.current
    if (held === null) return
    const replaced = held.getTracks().filter((entry) => entry.kind === track.kind)
    const next = new MediaStream([...held.getTracks().filter((entry) => entry.kind !== track.kind), track])
    for (const [remoteId, peer] of peersRef.current) {
      const sender = peer.getSenders().find((entry) => entry.track !== null && replaced.includes(entry.track))
      if (sender !== undefined) {
        void sender.replaceTrack(track).catch(() => undefined)
      } else {
        // In the stream this peer already has, or its far side shows the
        // microphone's stream and never the camera (see ontrack).
        peer.addTrack(track, sentStreamsRef.current.get(remoteId) ?? next)
        renegotiateRef.current(remoteId)
      }
    }
    replaced.forEach((entry) => {
      entry.onended = null
      entry.onmute = null
      entry.onunmute = null
      entry.stop()
    })
    localStreamRef.current = next
    setLocalStream(next)
    watchLocalRef.current(track)
  }, [])

  // Joining without a camera is not a decision for the whole meeting. The
  // camera is opened when it is first asked for and offered to every peer.
  const openingCameraRef = useRef(false)
  const setCameraEnabled = useCallback(
    (on: boolean) => {
      const tracks = localStreamRef.current?.getVideoTracks() ?? []
      if (tracks.length === 0) {
        if (!on || localStreamRef.current === null || openingCameraRef.current) return
        openingCameraRef.current = true
        const wanted = preferencesRef.current
        const generation = sessionGenerationRef.current
        void navigator.mediaDevices
          .getUserMedia({
            audio: false,
            video: wanted.videoDeviceId === undefined ? true : { deviceId: { exact: wanted.videoDeviceId } },
          })
          .then((opened) => {
            const [track] = opened.getVideoTracks()
            // The meeting it was asked for may be over, and another begun:
            // a camera nobody asked to show there is not shown there.
            if (track === undefined || localStreamRef.current === null || sessionGenerationRef.current !== generation) {
              opened.getTracks().forEach((entry) => entry.stop())
              return
            }
            track.contentHint = 'motion'
            publishLocalTrack(track)
            setCameraState(true)
            setVideoPaused(false)
            setDeviceNotice(null)
            announce()
          })
          .catch((caught: unknown) => {
            if (sessionGenerationRef.current !== generation) return
            setDeviceNotice(mediaErrorMessage(classifyMediaError(caught, pagePolicy(), true), true))
          })
          .finally(() => {
            openingCameraRef.current = false
          })
        return
      }
      tracks.forEach((track) => (track.enabled = on))
      setCameraState(on)
      if (on) setVideoPaused(false)
      announce()
    },
    [announce, publishLocalTrack],
  )

  // A device that stops under the call is opened again, once. A headset pulled
  // out ends its track for good; the browser's default is usually still there.
  const recoverLocalTrack = useCallback(
    (kind: 'audio' | 'video'): void => {
      if (localStreamRef.current === null) return
      const lost =
        kind === 'audio'
          ? 'Your microphone stopped, and nobody hears you.'
          : 'Your camera stopped, and nobody sees you.'
      const generation = sessionGenerationRef.current
      void navigator.mediaDevices
        .getUserMedia({ audio: kind === 'audio', video: kind === 'video' })
        .then((opened) => {
          const [track] = kind === 'audio' ? opened.getAudioTracks() : opened.getVideoTracks()
          // As with the camera: a device back after its meeting is not put
          // into the next one.
          if (track === undefined || localStreamRef.current === null || sessionGenerationRef.current !== generation) {
            opened.getTracks().forEach((entry) => entry.stop())
            return
          }
          const previous = localStreamRef.current.getTracks().find((entry) => entry.kind === kind)
          track.enabled = previous?.enabled ?? true
          if (kind === 'video') track.contentHint = 'motion'
          publishLocalTrack(track)
          setDeviceNotice(null)
          announce()
        })
        .catch(() => {
          if (sessionGenerationRef.current === generation) setDeviceNotice(lost)
        })
    },
    [announce, publishLocalTrack],
  )

  watchLocalRef.current = (track) => {
    const kind = track.kind === 'audio' ? 'audio' : 'video'
    track.onended = () => recoverLocalTrack(kind)
    // Muted by the system, not by the person: a phone call took the
    // microphone, or the browser paused capture in the background.
    track.onmute = () =>
      setDeviceNotice(
        kind === 'audio'
          ? 'Your microphone is paused by the system. Nobody hears you until it comes back.'
          : 'Your camera is paused by the system.',
      )
    track.onunmute = () => setDeviceNotice(null)
  }

  const retryDevices = useCallback(() => {
    const held = localStreamRef.current
    if (held === null) return
    setDeviceNotice(null)
    const ended = (kind: 'audio' | 'video'): boolean =>
      held.getTracks().some((track) => track.kind === kind && track.readyState === 'ended')
    if (ended('audio')) recoverLocalTrack('audio')
    if (ended('video')) recoverLocalTrack('video')
  }, [recoverLocalTrack])

  // A device plugged in or pulled out. The track that ended says so itself in
  // most browsers; this is for the ones where it only goes quiet.
  useEffect(() => {
    if (!connected || typeof navigator.mediaDevices?.addEventListener !== 'function') return
    const onChange = (): void => retryDevices()
    navigator.mediaDevices.addEventListener('devicechange', onChange)
    return () => navigator.mediaDevices.removeEventListener('devicechange', onChange)
  }, [connected, retryDevices])

  // Cameras go before audio does, and only once: someone who turns theirs back
  // on has overruled us, and is not overruled back a second later.
  const pausedOnceRef = useRef(false)
  // Once per meeting, not once per page: the next call starts with a camera.
  useEffect(() => {
    pausedOnceRef.current = false
    setVideoPaused(false)
  }, [callId])
  useEffect(() => {
    if (!constrained || pausedOnceRef.current) return
    if (!cameraEnabled) return
    const tracks = localStreamRef.current?.getVideoTracks() ?? []
    if (tracks.length === 0) return

    pausedOnceRef.current = true
    tracks.forEach((track) => (track.enabled = false))
    setCameraState(false)
    setVideoPaused(true)
    announce()
  }, [announce, cameraEnabled, constrained])

  const stopShare = useCallback(async (): Promise<void> => {
    const channel = channelRef.current
    if (localShareRef.current === null) return

    for (const [remoteId, senders] of shareSendersRef.current) {
      const peer = peersRef.current.get(remoteId)
      senders.forEach((sender) => {
        try {
          peer?.removeTrack(sender)
        } catch {
          // Already gone; the renegotiation below still settles it.
        }
      })
      renegotiateRef.current(remoteId)
    }
    shareSendersRef.current.clear()
    stopLocalShareTracks()

    await new Promise<void>((resolve) => {
      if (channel === null) {
        resolve()
        return
      }
      channel
        .push('share', { action: 'stop' })
        .receive('ok', () => resolve())
        .receive('error', () => resolve())
        .receive('timeout', () => resolve())
    })
  }, [stopLocalShareTracks])

  /**
   * Capture first, claim second. The browser's own picker is the last point
   * where a person can change their mind, and nothing should have been said
   * to the call before they have made it through that.
   */
  const startShare = useCallback(
    async (options: StartShareOptions = {}): Promise<void> => {
      const channel = channelRef.current
      if (channel === null || !screenShareSupported()) return

      setShareConflict(false)
      setShareError(null)

      let captured: MediaStream
      try {
        captured = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 15 },
          audio: options.audio === true,
        })
      } catch (caught) {
        // Cancelling the picker is the commonest path through here and is not
        // a failure worth a line of red. A refusal by the Space's policy or by
        // the operating system is: nothing else would tell the person why
        // their screen never appeared (calls-v1 §8).
        const message = displayErrorMessage(classifyDisplayError(caught, pagePolicy()))
        if (message !== null) setShareError(message)
        return
      }

      // The meeting may have ended while the person was choosing a source.
      // Never attach a late capture to a departed or replaced session.
      if (channelRef.current !== channel) {
        captured.getTracks().forEach(track => track.stop())
        return
      }

      // Text over motion: the encoder should hold detail rather than
      // smoothness when the two compete.
      captured.getVideoTracks().forEach((track) => (track.contentHint = 'detail'))

      // The browser knows what was picked; the meeting should say so rather
      // than call every share "a screen".
      const surface = (captured.getVideoTracks()[0]?.getSettings() as { displaySurface?: string } | undefined)
        ?.displaySurface
      const source: CallShareSource = surface === 'window' ? 'window' : surface === 'browser' ? 'tab' : 'screen'

      const claimed = await new Promise<'ok' | 'conflict' | 'error'>((resolve) => {
        channel
          .push('share', {
            action: 'start',
            source,
            audio: options.audio === true,
            takeover: options.takeover === true,
            stream_id: captured.id,
          })
          .receive('ok', () => resolve('ok'))
          .receive('error', (reply: { error?: { code?: string } }) =>
            resolve(reply?.error?.code === 'conflict' ? 'conflict' : 'error'),
          )
          .receive('timeout', () => resolve('error'))
      })

      if (channelRef.current !== channel) {
        captured.getTracks().forEach(track => track.stop())
        return
      }

      if (claimed !== 'ok') {
        captured.getTracks().forEach((track) => track.stop())
        if (claimed === 'conflict') setShareConflict(true)
        else setShareError('The screen could not be shared.')
        return
      }

      localShareRef.current = captured
      setLocalShare(captured)

      // Stopping from the browser's own bar is the same as stopping from
      // ours; the call is told either way.
      captured.getVideoTracks().forEach((track) => {
        track.onended = () => {
          void stopShare()
        }
      })

      for (const remoteId of peersRef.current.keys()) attachShareRef.current(remoteId)
    },
    [stopShare],
  )

  const dismissShareError = useCallback(() => {
    setShareConflict(false)
    setShareError(null)
  }, [])

  // What a peer's face is, and what its screen is, once the server has said
  // which stream is which. Everything that is not the screen is the face: a
  // client may send a camera started mid-call in a stream of its own, and
  // that is the same person's picture, not a second person or a screen.
  const remoteTracks = useMemo((): RemoteTrack[] => {
    const merged = mergedFacesRef.current
    const kept = new Set<UUID>()
    const tracks = Object.entries(remoteStreams).flatMap(([remoteId, streams]): RemoteTrack[] => {
      const screen = shareStreamOf(streams, remoteId, shareState)
      const faces = streams.filter((entry) => entry !== screen)
      if (faces.length <= 1) {
        const chosen = faces[0] ?? streams[0]
        return chosen === undefined ? [] : [{ userId: remoteId, stream: chosen }]
      }

      // Every voice, and the first picture still live. The first, so a
      // screen that lands a moment before the call says whose it is does not
      // take the place of a face already there.
      const audio = faces.flatMap((entry) => entry.getAudioTracks())
      const video =
        faces
          .map((entry) => entry.getVideoTracks().filter((track) => track.readyState !== 'ended'))
          .find((entry) => entry.length > 0) ?? []
      const contents = [...audio, ...video]
      const key = contents.map((track) => track.id).join(' ')
      kept.add(remoteId)
      const held = merged.get(remoteId)
      if (held !== undefined && held.key === key) return [{ userId: remoteId, stream: held.stream }]
      const stream = new MediaStream(contents)
      merged.set(remoteId, { key, stream })
      return [{ userId: remoteId, stream }]
    })
    for (const remoteId of [...merged.keys()]) if (!kept.has(remoteId)) merged.delete(remoteId)
    return tracks
  }, [remoteStreams, shareState])

  const remoteShare = useMemo((): MediaStream | null => {
    if (shareState === null || shareState.userId === userId) return null
    return shareStreamOf(remoteStreams[shareState.userId] ?? [], shareState.userId, shareState)
  }, [remoteStreams, shareState, userId])

  const share = useMemo(
    (): ShareSessionState => ({
      supported: screenShareSupported(),
      state: shareState,
      local: localShare,
      remote: remoteShare,
      conflict: shareConflict,
      error: shareError,
    }),
    [localShare, remoteShare, shareConflict, shareError, shareState],
  )

  return {
    localStream,
    remoteTracks,
    connected,
    peerStates,
    remoteMedia,
    error,
    micEnabled,
    cameraEnabled,
    speaking,
    constrained,
    videoPausedForBandwidth,
    share,
    sessionEnd,
    unreachable,
    deviceNotice,
    hangUp,
    setMicEnabled,
    setCameraEnabled,
    startShare,
    stopShare,
    dismissShareError,
    retryPeer,
    retryDevices,
  }
}
